// useExtendedCarDataListener.js
import { useState, useEffect, useRef } from "react";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc, // weiterhin benötigt, aber nur noch für Users
} from "firebase/firestore";
import { db } from "@/config/firebaseConfig";
import { useAuth } from "@/context/AuthContext";

const useExtendedCarDataListener = (statusFilter = "extended") => {
  const { user, authInitialized } = useAuth();
  const [carData, setCarData] = useState([]);
  const [loading, setLoading] = useState(true);

  const unsubscribeRef = useRef(null);
  const mergedMapRef = useRef({});
  const debounceTimeout = useRef(null);

  /* Debounce, um Render-Flut zu vermeiden */
  const flushToState = () => {
    clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      const arr = Object.values(mergedMapRef.current);
      if (!arr.length) {
        setCarData([]);
        setLoading(false);
        return;
      }
      arr.sort(
        (a, b) => new Date(b.lastUpdated || 0) - new Date(a.lastUpdated || 0)
      );
      setCarData(arr);
      setLoading(false);
    }, 300);
  };

  useEffect(() => {
    if (!authInitialized || !user) return;

    /* Vorherigen Listener aufräumen */
    unsubscribeRef.current?.();
    mergedMapRef.current = {};
    setCarData([]);
    setLoading(true);

    const userToFinCol = collection(db, "USERTOFIN");
    const q = query(userToFinCol, where("status", "==", statusFilter));

    unsubscribeRef.current = onSnapshot(
      q,
      (snap) => {
        snap.docChanges().forEach((change) => {
          const id = change.doc.id;
          const data = change.doc.data();

          if (change.type === "removed") {
            delete mergedMapRef.current[id];
            return;
          }

          /* added | modified */
          mergedMapRef.current[id] = { emailFinKey: id, ...data };

          /* Benutzer-Infos ergänzen (einmalig pro E-Mail) */
          if (data.email && !mergedMapRef.current[id].userLoaded) {
            getDoc(doc(db, "Users", data.email))
              .then((userDoc) => {
                if (userDoc.exists()) {
                  mergedMapRef.current[id] = {
                    ...mergedMapRef.current[id],
                    ...userDoc.data(),
                    userLoaded: true, // Flag, um Mehrfach-Fetch zu vermeiden
                  };
                  flushToState();
                }
              })
              .catch((err) => console.error("Error fetching Users doc:", err));
          }
        });

        /* Nach jedem Change-State flushen */
        flushToState();
      },
      (err) => console.error("Error in extended-cars listener:", err)
    );

    return () => {
      unsubscribeRef.current?.();
      clearTimeout(debounceTimeout.current);
    };
  }, [authInitialized, user, statusFilter]);

  return { carData, loading };
};

export default useExtendedCarDataListener;
